import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { Bouton, Etiquette, Liste, Pastille } from '../components/ui/Base'
import { EtatVide, MessageErreur } from '../components/ui/Etats'
import { Squelette } from '../components/ui/Squelette'
import { api } from '../lib/api/client'
import { cles } from '../lib/api/cles'
import type { Page, SemainePlanning, Utilisateur } from '../lib/api/types'
import { useSession } from '../lib/auth'
import { formatDate, formatDateLongue, formatDuree, formatHeure } from '../lib/formatters'
import { ajouterJours, aujourdhui, lundiDeLaSemaine } from '../lib/temps'

/**
 * Planning individuel, vue jour.
 *
 * Un collaborateur ou un remplaçant n'accède qu'à sa propre vue (R6) : le
 * sélecteur d'infirmier n'existe que pour un titulaire.
 */
export function PlanningInfirmier() {
  const [parametres, setParametres] = useSearchParams()
  const { utilisateur, estTitulaire } = useSession()

  const date = parametres.get('date') ?? aujourdhui()
  const utilisateurId = estTitulaire
    ? (parametres.get('utilisateur_id') ?? utilisateur?.id ?? '')
    : (utilisateur?.id ?? '')

  const lundi = lundiDeLaSemaine(date)

  const requete = useQuery({
    queryKey: cles.planning.semaine(lundi),
    queryFn: ({ signal }) => api.get<SemainePlanning>(`/planning/semaine?lundi=${lundi}`, signal),
  })

  const requeteInfirmiers = useQuery({
    queryKey: cles.utilisateurs.liste(),
    queryFn: ({ signal }) => api.get<Page<Utilisateur>>('/utilisateurs', signal),
    enabled: estTitulaire,
  })

  const jour = requete.data?.jours.find((j) => j.date === date)

  const interventions = useMemo(
    () => (jour?.interventions ?? []).filter((i) => i.utilisateur_id === utilisateurId),
    [jour, utilisateurId],
  )

  const totaux = useMemo(
    () => ({
      nb: interventions.length,
      duree: interventions.reduce((s, i) => s + i.duree_minutes, 0),
      trajet: interventions.reduce((s, i) => s + (i.duree_trajet_minutes ?? 0), 0),
    }),
    [interventions],
  )

  function changer(cle: string, valeur: string) {
    const suivants = new URLSearchParams(parametres)
    if (valeur) suivants.set(cle, valeur)
    else suivants.delete(cle)
    setParametres(suivants, { replace: true })
  }

  const infirmier = requete.data?.infirmiers.find((i) => i.id === utilisateurId)

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">
            {estTitulaire && infirmier ? `Planning de ${infirmier.prenom} ${infirmier.nom}` : 'Mon planning'}
          </h1>
          <p className="text-sm capitalize text-slate-600">{formatDateLongue(date)}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {estTitulaire ? (
            <Liste
              aria-label="Infirmier"
              value={utilisateurId}
              onChange={(e) => changer('utilisateur_id', e.target.value)}
            >
              {(requeteInfirmiers.data?.items ?? []).map((u) => (
                <option key={u.id} value={u.id}>
                  {u.prenom} {u.nom}
                </option>
              ))}
            </Liste>
          ) : null}
          <Bouton variante="secondaire" onClick={() => changer('date', ajouterJours(date, -1))} aria-label="Jour précédent">←</Bouton>
          <Bouton variante="secondaire" onClick={() => changer('date', aujourdhui())}>Aujourd&apos;hui</Bouton>
          <Bouton variante="secondaire" onClick={() => changer('date', ajouterJours(date, 1))} aria-label="Jour suivant">→</Bouton>
          <input
            type="date"
            value={date}
            aria-label="Choisir une date"
            onChange={(e) => changer('date', e.target.value)}
            className="rounded-md border border-slate-300 px-2 py-1.5 text-sm"
          />
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <Statistique titre="Interventions" valeur={String(totaux.nb)} />
        <Statistique titre="Durée cumulée" valeur={formatDuree(totaux.duree)} />
        <Statistique titre="Trajets cumulés" valeur={formatDuree(totaux.trajet)} />
      </div>

      {requete.isPending ? (
        <Squelette lignes={8} />
      ) : requete.isError ? (
        <MessageErreur erreur={requete.error} onReessayer={() => requete.refetch()} />
      ) : interventions.length === 0 ? (
        <EtatVide
          icone="☀️"
          titre="Aucune intervention ce jour"
          description="Rien n’est planifié à cette date pour cet infirmier."
        />
      ) : (
        <ol className="space-y-2">
          {interventions.map((intervention, index) => (
            <li key={intervention.id}>
              {/* Le temps de trajet s'affiche ENTRE deux interventions : c'est
                  là qu'il a un sens, pas accolé à l'une des deux. */}
              {index > 0 && intervention.duree_trajet_minutes ? (
                <p className="py-1 pl-16 text-xs text-slate-500">
                  ↓ {formatDuree(intervention.duree_trajet_minutes)} de trajet
                </p>
              ) : null}

              <div className="flex gap-3 rounded-lg border border-slate-200 bg-white p-3">
                <div className="w-14 shrink-0 text-right">
                  <p className="font-semibold tabular-nums text-slate-900">
                    {formatHeure(intervention.heure_debut)}
                  </p>
                  <p className="text-xs tabular-nums text-slate-500">
                    {formatHeure(intervention.heure_fin)}
                  </p>
                </div>

                <div
                  className="w-1 shrink-0 rounded"
                  style={{ backgroundColor: infirmier?.couleur ?? '#94a3b8' }}
                  aria-hidden="true"
                />

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <Link
                      to={`/patients/${intervention.patient_id}`}
                      className="font-medium text-sky-700 hover:underline"
                    >
                      {intervention.patient_nom} {intervention.patient_prenom}
                    </Link>
                    <span className="text-xs text-slate-500">{intervention.patient_ville}</span>
                    <Etiquette ton={intervention.statut === 'REALISEE' ? 'succes' : 'neutre'}>
                      {intervention.statut}
                    </Etiquette>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {intervention.soins.map((soin) => (
                      <span
                        key={soin.type_soin_id}
                        className="flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700"
                      >
                        <Pastille couleur={soin.couleur} />
                        {soin.libelle}
                      </span>
                    ))}
                  </div>
                </div>

                <p className="shrink-0 text-xs text-slate-500">
                  {formatDuree(intervention.duree_minutes)}
                </p>
              </div>
            </li>
          ))}
        </ol>
      )}

      <p className="text-sm text-slate-600">
        Semaine du {formatDate(lundi)} —{' '}
        <Link to={`/planning?lundi=${lundi}`} className="text-sky-700 hover:underline">
          voir le planning global
        </Link>
      </p>
    </div>
  )
}

function Statistique({ titre, valeur }: { titre: string; valeur: string }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <p className="text-xs uppercase tracking-wide text-slate-500">{titre}</p>
      <p className="mt-1 text-xl font-semibold tabular-nums text-slate-900">{valeur}</p>
    </div>
  )
}
